import { parseAuthentication, parseRegistration } from "./parsers.js";
import { AuthenticationEncoded, AuthenticationParsed, CredentialKey, NamedAlgo, RegistrationEncoded, RegistrationParsed } from "./types.js";
import * as utils from './utils.js'

/**
 * 检查给定的值是否有效。
 * @param validator 验证器，可以是函数或单一值。
 * @param value 需要验证的值。
 * @returns 返回一个布尔值的Promise，表示值是否有效。
 */
async function isValid(validator :any, value :any) :Promise<boolean> {
    if(typeof validator === 'function') {
        const res = validator(value)
        if(res instanceof Promise)
            return await res
        else
            return res
    }
    // 验证器也可以是一个单一的值
    return validator === value
}

/**
 * 检查给定的值是否无效。
 * @param validator 验证器，可以是函数或单一值。
 * @param value 需要验证的值。
 * @returns 返回一个布尔值的Promise，表示值是否无效。
 */
async function isNotValid(validator :any, value :any) :Promise<boolean> {
    return !(await isValid(validator, value))
}

/**
 * 注册检查接口，定义了注册流程中的检查项。
 */
interface RegistrationChecks {
    challenge: string | Function, // 挑战值，可以是字符串或验证函数
    origin: string | Function // 来源，可以是字符串或验证函数
}

/**
 * 验证注册信息的正确性。
 * @param registrationRaw 待验证的注册信息。
 * @param expected 预期的检查项。
 * @returns 返回经过解析的注册信息。
 */
export async function verifyRegistration(registrationRaw: RegistrationEncoded, expected: RegistrationChecks): Promise<RegistrationParsed> {
    const registration = parseRegistration(registrationRaw)
    registration.client.challenge

    if (registration.client.type !== "webauthn.create")
        throw new Error(`Unexpected ClientData type: ${registration.client.type}`)

    // 对来源和挑战值进行验证
    if (await isNotValid(expected.origin, registration.client.origin))
        throw new Error(`Unexpected ClientData origin: ${registration.client.origin}`)

    if (await isNotValid(expected.challenge, registration.client.challenge))
        throw new Error(`Unexpected ClientData challenge: ${registration.client.challenge}`)

    return registration
}

/**
 * 认证检查接口，定义了认证流程中的检查项。
 */
interface AuthenticationChecks {
    challenge: string | Function, // 挑战值，可以是字符串或验证函数
    origin: string | Function, // 来源，可以是字符串或验证函数
    userVerified: boolean, // 用户是否已验证
    counter?: number, // 可选的计数器值
    verbose?: boolean // 是否详细输出
}

/**
 * 验证认证信息的正确性。
 * @param authenticationRaw 待验证的认证信息。
 * @param credential 密钥凭证。
 * @param expected 预期的检查项。
 * @returns 返回经过解析的认证信息。
 */
export async function verifyAuthentication(authenticationRaw: AuthenticationEncoded, credential: CredentialKey, expected: AuthenticationChecks): Promise<AuthenticationParsed> {
    if (authenticationRaw.credentialId !== credential.id)
        throw new Error(`Credential ID mismatch: ${authenticationRaw.credentialId} vs ${credential.id}`)

    const isValidSignature: boolean = await verifySignature({
        algorithm: credential.algorithm,
        publicKey: credential.publicKey,
        authenticatorData: authenticationRaw.authenticatorData,
        clientData: authenticationRaw.clientData,
        signature: authenticationRaw.signature,
        verbose: expected.verbose
    })

    if(!isValidSignature)
        throw new Error(`Invalid signature: ${authenticationRaw.signature}`)

    const authentication = parseAuthentication(authenticationRaw)

    if (authentication.client.type !== "webauthn.get")
        throw new Error(`Unexpected clientData type: ${authentication.client.type}`)

    // 对来源和挑战值进行验证
    if (await isNotValid(expected.origin, authentication.client.origin))
        throw new Error(`Unexpected ClientData origin: ${authentication.client.origin}`)

    if (await isNotValid(expected.challenge, authentication.client.challenge))
        throw new Error(`Unexpected ClientData challenge: ${authentication.client.challenge}`)

    // 验证RpIdHash的一致性
    const rpId = new URL(authentication.client.origin).hostname
    const expectedRpIdHash = utils.toBase64url(await utils.sha256(utils.toBuffer(rpId)))
    if (authentication.authenticator.rpIdHash !== expectedRpIdHash)
        throw new Error(`Unexpected RpIdHash: ${authentication.authenticator.rpIdHash} vs ${expectedRpIdHash}`)

    // 检查用户存在性和验证状态
    if (!authentication.authenticator.flags.userPresent)
        throw new Error(`Unexpected authenticator flags: missing userPresent`)

    if (!authentication.authenticator.flags.userVerified && expected.userVerified)
        throw new Error(`Unexpected authenticator flags: missing userVerified`)

    // 检查计数器值
    if (expected.counter && authentication.authenticator.counter <= expected.counter)
        throw new Error(`Unexpected authenticator counter: ${authentication.authenticator.counter} (should be > ${expected.counter})`)

    return authentication
}


/*
 * 根据指定的算法名称获取算法参数。
 * 支持的算法有 'RS256' 和 'ES256'。
 *
 * @param algorithm 算法名称，可选值为 'RS256' 和 'ES256'。
 * @returns 返回对应算法的参数对象。
 * @throws 如果传入的算法名称不受支持，则抛出错误。
 */
function getAlgoParams(algorithm: NamedAlgo): any {
    switch (algorithm) {
        case 'RS256':
            return {
                name: 'RSASSA-PKCS1-v1_5',
                hash: 'SHA-256'
            };
        case 'ES256':
            return {
                name: 'ECDSA',
                namedCurve: 'P-256',
                hash: 'SHA-256',
            };
        // case 'EdDSA': 不受浏览器支持
        default:
            throw new Error(`Unknown or unsupported crypto algorithm: ${algorithm}. Only 'RS256' and 'ES256' are supported.`)
    }
}


type AlgoParams = AlgorithmIdentifier | RsaHashedImportParams | EcKeyImportParams | HmacImportParams | AesKeyAlgorithm

/*
 * 根据算法参数和公钥字符串导入CryptoKey。
 *
 * @param algoParams 算法参数，用于指定导入密钥时的算法配置。
 * @param publicKey 公钥字符串，Base64url编码。
 * @returns 返回一个Promise，解析为导入的CryptoKey对象。
 */
async function parseCryptoKey(algoParams: AlgoParams, publicKey: string): Promise<CryptoKey> {
    const buffer = utils.parseBase64url(publicKey)
    return crypto.subtle.importKey('spki', buffer, algoParams, false, ['verify'])
}

type VerifyParams = {
    algorithm: NamedAlgo,
    publicKey: string, // Base64url encoded
    authenticatorData: string, // Base64url encoded
    clientData: string, // Base64url encoded
    signature: string, // Base64url encoded
    verbose?: boolean, // Enables debug logs containing sensitive data like crypto keys
}


/*
 * 验证签名是否有效。
 *
 * @param algorithm 算法名称，必须是支持的算法。
 * @param publicKey 公钥字符串，Base64url编码，用于验证签名。
 * @param authenticatorData 可信认证数据，Base64url编码。
 * @param clientData 客户端数据，Base64url编码。
 * @param signature 签名值，Base64url编码。
 * @param verbose 是否开启详细模式，若开启，会输出更多的调试日志。
 * @returns 返回一个Promise，解析为一个布尔值，表示签名是否有效。
 */
export async function verifySignature({ algorithm, publicKey, authenticatorData, clientData, signature, verbose }: VerifyParams): Promise<boolean> {
    const algoParams = getAlgoParams(algorithm)
    let cryptoKey = await parseCryptoKey(algoParams, publicKey)

    if(verbose) {
        console.debug(cryptoKey)
    }

    let clientHash = await utils.sha256(utils.parseBase64url(clientData));

    // 在“登录”时，authenticatorData的长度正好是37字节
    let comboBuffer = utils.concatenateBuffers(utils.parseBase64url(authenticatorData), clientHash)

    if(verbose) {
        console.debug('Crypto Algo: ' + JSON.stringify(algoParams))
        console.debug('Public key: ' + publicKey)
        console.debug('Data: ' + utils.toBase64url(comboBuffer))
        console.debug('Signature: ' + signature)
    }

    // 解码签名值，对于ES256算法需要将ASN.1格式转换为"raw"格式
    let signatureBuffer = utils.parseBase64url(signature)
    if(algorithm == 'ES256')
        signatureBuffer = convertASN1toRaw(signatureBuffer)

    const isValid = await crypto.subtle.verify(algoParams, cryptoKey, signatureBuffer, comboBuffer)

    return isValid
}

/*
 * 将ASN.1格式的签名转换为"raw"格式。
 *
 * @param signatureBuffer ASN.1格式的签名数组缓冲区。
 * @returns 返回转换后的"raw"格式签名的Uint8Array。
 */
function convertASN1toRaw(signatureBuffer :ArrayBuffer) {
    // 将签名从ASN.1序列转换为"raw"格式
    const usignature = new Uint8Array(signatureBuffer);
    const rStart = usignature[4] === 0 ? 5 : 4;
    const rEnd = rStart + 32;
    const sStart = usignature[rEnd + 2] === 0 ? rEnd + 3 : rEnd + 2;
    const r = usignature.slice(rStart, rEnd);
    const s = usignature.slice(sStart);
    return new Uint8Array([...r, ...s]);
}
